/* eslint-disable no-nested-ternary */
import { ArchivedOrderItem, LegalEntity, OrderItem, PromotionApplied } from '../apiTypes';

type Props = {
  orderItems: OrderItem[];
  legalEntities: DbDocument<LegalEntity>[];
  service?: number;
  discount?: number;
  promotionsApplied: PromotionApplied[];
};

type BillItem = {
  itemsTotalSum: number;
  promotionsSum: number;
  discountSum: number;
  serviceSum: number;
  toPay: number;
};

const defaultBillItem: BillItem = {
  itemsTotalSum: 0,
  toPay: 0,
  promotionsSum: 0,
  discountSum: 0,
  serviceSum: 0,
};

type BillCalcByEntity = Record<string, BillItem>;

export type BillCalc = {
  total: BillItem;
  byLegalEntity: BillCalcByEntity;
};

export const getItemsByLegalEntityIds = (orderItems: OrderItem[], defaultLegalEntityId = 'default') =>
  orderItems.reduce<Record<string, OrderItem[]>>((acc, item) => {
    const key = item.legalEntityId || defaultLegalEntityId;
    return {
      ...acc,
      [key]: [...(acc[key] || []), item],
    };
  }, {});

export const getOrderItemPriceWithMods = (item: OrderItem | ArchivedOrderItem) =>
  item.modifications && item.modifications.length
    ? item.modifications.reduce((acc, mod) => acc + (mod.price || 0), Number(item.price || 0))
    : Number(item.price || 0);

export const getOrderItemTotalPrice = (item: OrderItem | ArchivedOrderItem) => {
  const basePrice = getOrderItemPriceWithMods(item);

  const itemTotal = item.weight
    ? Math.round((basePrice / Number(item.defaultWeight || 0)) * Number(item.weight || 0))
    : basePrice;
  return itemTotal;
};

const getBillCalc = ({ orderItems, legalEntities, service = 0, discount = 0, promotionsApplied }: Props): BillCalc => {
  const itemsByLegalEntityIds = getItemsByLegalEntityIds(orderItems, legalEntities[0]?.id);

  const billCalcByLegalEntity = Object.entries(itemsByLegalEntityIds).reduce<BillCalcByEntity>((acc, [key, items]) => {
    const billItem = items.reduce<BillItem>((billItemAcc, item) => {
      const promoApplied = promotionsApplied.find((p) => p.appliedForOrderItemIds.includes(item.id));

      const itemTotal = getOrderItemTotalPrice(item);

      const itemPromoSum = Math.round(
        promoApplied
          ? promoApplied.discountType === 'percentage'
            ? (itemTotal * Number(promoApplied.discountValue)) / 100
            : promoApplied.discountSum / promoApplied.appliedForOrderItemIds.length
          : 0
      );

      const itemDiscountSum = Math.round(Number(discount ? (discount * (itemTotal - itemPromoSum)) / 100 : 0));

      const itemServiceSum = Math.round(
        Number(service ? (service * (itemTotal - itemPromoSum - itemDiscountSum)) / 100 : 0)
      );

      const itemToPay = itemTotal - itemPromoSum - itemDiscountSum + itemServiceSum;

      return {
        itemsTotalSum: billItemAcc.itemsTotalSum + itemTotal,
        toPay: billItemAcc.toPay + itemToPay,
        promotionsSum: billItemAcc.promotionsSum + itemPromoSum,
        discountSum: billItemAcc.discountSum + itemDiscountSum,
        serviceSum: billItemAcc.serviceSum + itemServiceSum,
      };
    }, defaultBillItem);

    return {
      ...acc,
      [key]: billItem,
    };
  }, {});

  const totalBillCalc: BillItem = Object.values(billCalcByLegalEntity).reduce<BillItem>(
    (acc, billItem) => ({
      itemsTotalSum: acc.itemsTotalSum + billItem.itemsTotalSum,
      toPay: acc.toPay + billItem.toPay,
      promotionsSum: acc.promotionsSum + billItem.promotionsSum,
      discountSum: acc.discountSum + billItem.discountSum,
      serviceSum: acc.serviceSum + billItem.serviceSum,
    }),
    defaultBillItem
  );

  return {
    total: totalBillCalc,
    byLegalEntity: billCalcByLegalEntity,
  };
};

export default getBillCalc;
